import { useState, useEffect } from "react";
import { validateField } from "../utils/validateFields";
import { getCompanySalesReps } from "../services/userService";
import { AUTHORIZE_NET_COMPANY } from "../utils/constants";
import _ from "lodash";

/**
 * Initial form state with default values
 * Contains all the necessary fields for merchant registration
 */
const initialState = {
  // Personal Information
  firstName: "",
  lastName: "",
  businessName: "",
  email: "",
  phoneNumber: "",
  
  // Business Details
  Gateway: "",
  company: "",
  salesRep: "",
  accountExecutive: "",
  repEmail: "",
  subRep: "",
  notes: "",
  reason: "",
  projectedVolume: "",
  
  // Documents
  file: null,
  
  // Agent Lists
  [AUTHORIZE_NET_COMPANY]: [],
  CyberSource: [],
  CyberSourceSales: [],
  preferredAgent: [],
  
  // Additional Settings
  currency: "United States",
  rateQuote: "No",
  errors: {},
};

/**
 * Custom hook for managing merchant registration form
 * @param {Object} config - Configuration object for the form
 * @param {boolean} config.hasCompanySelect - Whether to show company selection
 * @param {string} config.company - Default company name
 * @param {string} config.apiCompanyName - Company name for API calls
 * @returns {Object} Form state and handlers
 */
export const useMerchantForm = (config) => {
  // Initialize state with config-based defaults
  const [state, setState] = useState({
    ...initialState,
    company: config?.hasCompanySelect ? AUTHORIZE_NET_COMPANY : config?.company || "",
    salesRep: config?.hasCompanySelect ? "Select Sales Rep" : "",
    accountExecutive: config?.hasCompanySelect ? "Select Account Executive" : "",
    currency: config?.hasLocation ? "Select Location" : "",
  });

  // Fetch and organize agent data based on configuration
  useEffect(() => {
    // Don't make API calls for the default landing page or if config is missing
    if (!config || !config.apiCompanyName || config.isDefaultLanding) return;

    const updateAgentState = (agents, companyKey) => {
      // Ensure agents is always an array
      const safeAgents = Array.isArray(agents) ? agents : [];
      
      setState(prev => {
        // Check if we already have agents for this company
        if (prev[companyKey]?.length > 0) {
          return prev;
        }
        return {
          ...prev,
          [companyKey]: [...(prev[companyKey] || []), ...safeAgents]
        };
      });
    };

    // Fetch sales agents (users with Sales Agent role) for companies
    const fetchAgents = async () => {
      try {
        if (config.hasCompanySelect) {
          // Fetch sales reps for Authorize.net and CyberSource
          const [authorizeNetAgents, cyberSourceAgents] = await Promise.all([
            getCompanySalesReps(AUTHORIZE_NET_COMPANY).catch(() => []),
            getCompanySalesReps('CyberSource').catch(() => [])
          ]);
          
          // Update state with fetched agents
          updateAgentState(authorizeNetAgents, AUTHORIZE_NET_COMPANY);
          updateAgentState(cyberSourceAgents, 'CyberSource');
          // CyberSourceSales will be same as CyberSource
          updateAgentState(cyberSourceAgents, 'CyberSourceSales');
        } else if (config.apiCompanyName && config.apiCompanyName !== 'Website') {
          // Fetch sales reps for the specific company
          const agents = await getCompanySalesReps(config.apiCompanyName).catch(() => []);
          updateAgentState(agents, 'preferredAgent');
        }
      } catch (error) {
        console.error('Error fetching sales agents:', error);
      }
    };

    fetchAgents();
  }, [config]);

  /**
   * Handle input field changes
   * @param {Event} e - Change event
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    // If the field is phoneNumber, strip all non-numeric characters
    if (name === "phoneNumber") {
      newValue = value.replace(/\D/g, "");
    }

    // Validate the field
    const errorMsg = validateField(name, newValue, { ...state, [name]: newValue }, config);

    setState(prev => ({
      ...prev,
      [name]: newValue,
      errors: {
        ...prev.errors,
        [name]: errorMsg // Will be undefined if valid, which removes the error
      }
    }));
  };

  /**
   * Handle sales representative selection
   * Updates sales rep and associated email
   * @param {Event} event - Change event
   */
  const handleSelectChange = (event) => {
    const value = event.target.value;
    let errorMsg;

    if (event.target.name === "salesRep") {
      errorMsg = validateField("salesRep", value, { ...state, salesRep: value }, config);
    }

    const agents = config.hasCompanySelect
      ? state.company === AUTHORIZE_NET_COMPANY ? state[AUTHORIZE_NET_COMPANY] : state.CyberSource
      : state.preferredAgent;

    const selectedAgent = agents.find(agent => agent.name === value);
    
    setState(prev => ({
      ...prev,
      salesRep: value,
      repEmail: selectedAgent?.email || "",
      subRep: state.company === "CyberSource" ? "" : value,
      errors: {
        ...prev.errors,
        salesRep: errorMsg
      }
    }));
  };

  /**
   * Reset form to initial state while preserving agent lists
   */
  const resetForm = () => {
    setState(prev => ({
      ...initialState,
      company: config?.hasCompanySelect ? AUTHORIZE_NET_COMPANY : config?.company || "",
      salesRep: config?.hasCompanySelect ? "Select Sales Rep" : "",
      accountExecutive: config?.hasCompanySelect ? "Select Account Executive" : "",
      [AUTHORIZE_NET_COMPANY]: prev[AUTHORIZE_NET_COMPANY],
      CyberSource: prev.CyberSource,
      CyberSourceSales: prev.CyberSourceSales,
      preferredAgent: prev.preferredAgent,
      errors: {},
    }));
  };

  return {
    state,
    setState,
    handleChange,
    handleSelectChange,
    resetForm
  };
}; 